// ─────────────────────────────────────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────────────────────────────────────

export type Tier = 0 | 1 | 2 | 3 | 4 | 5 | 6;

// ─────────────────────────────────────────────────────────────────────────────
// State (persisted to localStorage so reopening lands you where you left off)
// ─────────────────────────────────────────────────────────────────────────────

/** Where a body with no elevation at any level stops descending. */
export interface ImageryFloor {
  /** The albedo pyramid's deepest level, off the row's own bake block. */
  readonly level: number;
  /** Metres per albedo cell at that level. */
  readonly gsdM: number;
  /** Where the descent stops, metres above the datum. */
  readonly altitudeM: number;
}

export interface AtlasState {
  tier: Tier;
  bodyId: string | null;
  quadId: string | null;
  cellId: string | null;
  /** Which plot of the claim we have descended into, as "q_r". */
  tileId: string | null;
}

const STORAGE_KEY = 'atlas.lastView';

/** The body the Atlas opens on when the URL names none. */
const URL_BODY_DEFAULT = 'mars';

/**
 * The body the URL asks for.
 *
 * `?body=<id>`, and the default when the URL is silent. The ladder below the
 * orrery is about one body - its globe, its pyramid, its sky and its sites -
 * and the registry fetch is a round trip, so this is what says which one
 * before there is a row to read. `explicit` is what makes an unknown id a
 * refusal rather than a default: a link that names a body the registry does
 * not have is a typo, and opening the default in its place would be a silent
 * lie about which ground is on screen.
 */
export function urlBody(): { id: string; explicit: boolean } {
  const raw = new URLSearchParams(location.search).get('body');
  return raw ? { id: raw, explicit: true } : { id: URL_BODY_DEFAULT, explicit: false };
}

/**
 * The site the URL asks for.
 *
 * `?site=<slug>`, and null when the URL names none, which is the body's first
 * row the way it always was. Refused the way `?body=` is: a slug the open
 * body has no row for is a typo, and opening its first site instead would be
 * a silent lie about which ground the ladder is aimed at.
 */
export function urlSite(): string | null {
  const raw = new URLSearchParams(location.search).get('site');
  return raw !== null && raw !== '' ? raw : null;
}

export function loadState(): AtlasState {
  // Always start at the heliocentric overview when the tab opens. Persisted
  // state was confusing — players would re-enter at T2 (quadrangle view) and
  // think the quad outlines were a regression on the solar-system screen.
  // In-session navigation still moves freely between tiers.
  //
  // One exception: an explicit ?atlasTier= in the URL. atlas-00 §8 wants views
  // to be shareable by link and §13 makes "copy the URL, open it elsewhere, see
  // the same thing" a condition of done. This is the first thread of that, and
  // it is also how the deep tiers get tested without five clicks every time.
  const want = urlBody();
  const tier = Number(new URLSearchParams(location.search).get('atlasTier'));
  if (Number.isFinite(tier) && tier >= 1 && tier <= 6) {
    return {
      tier: tier as Tier,
      bodyId: want.id,
      // Which quad opens is not knowable here: it follows the site registry,
      // which the mount fetches. Left null, the camera frames whichever one it
      // turns out to be.
      quadId: null,
      // Which cell opens follows the anchor and the drawn resolution, both
      // of which land with the registry, so a URL cannot name one.
      cellId: null,
      tileId: null,
    };
  }
  // A body named in the URL opens the orrery focused on it; with none named the
  // Atlas opens where it always has, at the heliocentric view.
  if (want.explicit) {
    return { tier: 1, bodyId: want.id, quadId: null, cellId: null, tileId: null };
  }
  return { tier: 0, bodyId: null, quadId: null, cellId: null, tileId: null };
}

export function saveState(_s: AtlasState): void {
  // No-op for the slice. (Reserved for later — could persist the last hex
  // visited, etc., once the deeper tiers carry real game state.)
}
